import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@material-ui/core";
import { BRAND } from "src/theme/brandTokens";
import ItemsByCompanyV2 from "./ItemsByCompanyV2";
import ProximosPasosListaV2 from "./ProximosPasosListaV2";
import { formatearMonedaCliente } from "src/utils/formatearMonedaCliente";
import { splitMontoMoneda } from "src/utils/splitMontoMonedaCliente";
import {
  modalidadItem,
  MODALIDAD_META,
  ORDEN_MODALIDAD,
} from "src/utils/propuestaItemLabels";

// ─── Helpers ────────────────────────────────────────────────────────────────

function padNumero(n) {
  return String(n).padStart(2, "0");
}

// ─── Sub-componentes ────────────────────────────────────────────────────────

/**
 * Renderiza texto plano dividido por `\n\n` (separador de párrafo) y `\n`
 * (salto suave) en una secuencia de `<p>` con margen entre párrafos.
 */
const ParrafosV2 = ({ texto, italica }) => {
  if (!texto) return null;
  const parrafos = String(texto)
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <>
      {parrafos.map((parrafo, i) => (
        <Box
          component="p"
          key={i}
          sx={{
            m: 0,
            mb: "12pt",
            textIndent: 0,
            fontStyle: italica ? "italic" : "normal",
            color: italica ? BRAND.color.grey70 : "inherit",
            "&:last-child": { mb: 0 },
          }}
        >
          {parrafo.split("\n").map((linea, idx, arr) => (
            <React.Fragment key={idx}>
              {linea}
              {idx < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </Box>
      ))}
    </>
  );
};

/** Cuadrado naranja decorativo a la izquierda del título de sección. */
const IconoSeccion = () => (
  <Box
    aria-hidden
    sx={{
      width: "18pt",
      height: "18pt",
      bgcolor: BRAND.color.primary,
      flexShrink: 0,
    }}
  />
);

/**
 * Sección interna: eyebrow numerado uppercase + cuadrado ícono naranja
 * junto al título mega + línea divisoria sutil + body. Mantiene paridad
 * visual con la composición de página interna del PDF de propuesta.
 *
 * Si recibe `numero` y `titulo`, el eyebrow se compone como `NN · TÍTULO`.
 * Si recibe `eyebrow` explícito, lo usa tal cual (caso del índice).
 */
const SeccionV2 = ({ numero, titulo, eyebrow, children }) => {
  const eyebrowText =
    eyebrow != null
      ? eyebrow
      : numero != null
        ? `${padNumero(numero)} · ${String(titulo).toUpperCase()}`
        : String(titulo).toUpperCase();

  return (
    <Box
      component="section"
      sx={{ mb: { xs: 6, md: 10 }, scrollMarginTop: "24px" }}
    >
      <Box
        sx={{
          fontFamily: BRAND.font.family.base,
          fontSize: BRAND.font.size.eyebrow,
          color: BRAND.color.primary,
          textTransform: "uppercase",
          letterSpacing: BRAND.font.letterSpacing.widest,
          fontWeight: BRAND.font.weight.medium,
          mb: "6mm",
        }}
      >
        {eyebrowText}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10pt",
          mb: "8mm",
        }}
      >
        <IconoSeccion />
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontFamily: BRAND.font.family.display,
            fontSize: BRAND.font.size.h2,
            fontWeight: BRAND.font.weight.black,
            color: BRAND.color.inkSoft,
            letterSpacing: BRAND.font.letterSpacing.tight,
            lineHeight: BRAND.font.lineHeight.snug,
          }}
        >
          {titulo}
        </Typography>
      </Box>
      <Box
        component="hr"
        sx={{
          border: 0,
          borderTop: `1px solid ${BRAND.color.divider}`,
          m: 0,
          mb: "10mm",
        }}
      />
      <Box
        sx={{
          fontFamily: BRAND.font.family.base,
          fontSize: BRAND.font.size.body,
          color: BRAND.color.inkSoft,
          lineHeight: BRAND.font.lineHeight.body,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const EquipoBody = ({ equipo }) => {
  const ordenado = [...equipo].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 3,
      }}
    >
      {ordenado.map((miembro, idx) => (
        <Box
          key={idx}
          sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
        >
          {miembro.fotoUrl ? (
            <Box
              component="img"
              src={miembro.fotoUrl}
              alt={miembro.nombre}
              sx={{
                width: 56,
                height: 56,
                objectFit: "cover",
                display: "block",
                flexShrink: 0,
              }}
            />
          ) : (
            <Box
              aria-hidden
              sx={{
                width: 56,
                height: 56,
                bgcolor: BRAND.color.primary,
                color: BRAND.color.paper,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: BRAND.font.family.display,
                fontWeight: BRAND.font.weight.bold,
                fontSize: BRAND.font.size.bodyLarge,
                flexShrink: 0,
              }}
            >
              {(miembro.nombre || "?")[0].toUpperCase()}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontFamily: BRAND.font.family.display,
                fontWeight: BRAND.font.weight.bold,
                color: BRAND.color.inkSoft,
                fontSize: BRAND.font.size.bodyLarge,
                mb: 0.5,
              }}
            >
              {miembro.nombre}
            </Box>
            {miembro.rolDisplay && (
              <Box
                sx={{
                  fontSize: BRAND.font.size.eyebrow,
                  color: BRAND.color.primary,
                  textTransform: "uppercase",
                  letterSpacing: BRAND.font.letterSpacing.wide,
                  fontWeight: BRAND.font.weight.medium,
                  mb: 1,
                }}
              >
                {miembro.rolDisplay}
              </Box>
            )}
            {miembro.descripcionExperiencia && (
              <Box
                sx={{
                  fontSize: BRAND.font.size.bodySmall,
                  color: BRAND.color.grey70,
                  lineHeight: BRAND.font.lineHeight.body,
                }}
              >
                <ParrafosV2 texto={miembro.descripcionExperiencia} />
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const EtiquetaCampo = ({ children }) => (
  <Box
    sx={{
      fontSize: BRAND.font.size.eyebrow,
      color: BRAND.color.primary,
      textTransform: "uppercase",
      letterSpacing: BRAND.font.letterSpacing.wide,
      fontWeight: BRAND.font.weight.medium,
      mb: 1,
    }}
  >
    {children}
  </Box>
);

/** Grupos del desglose económico, en el mismo orden que el PDF. */
const GRUPOS_DESGLOSE = [
  { grupo: "RECURRENTE", titulo: "Servicios recurrentes" },
  { grupo: "UNICO", titulo: "Pago único" },
];

const DesgloseGrupoEyebrow = ({ children }) => (
  <Box
    sx={{
      fontSize: BRAND.font.size.eyebrow,
      color: BRAND.color.primary,
      textTransform: "uppercase",
      letterSpacing: BRAND.font.letterSpacing.widest,
      fontWeight: BRAND.font.weight.medium,
      mb: "3mm",
    }}
  >
    {children}
  </Box>
);

const DesgloseLineaItem = ({ item }) => {
  const nombre = item.nombreServicio || item.faseTitulo || "Servicio";
  const { monto, sigla } = splitMontoMoneda(
    item.valorConDescuento,
    item.tipoMoneda,
  );
  const inline = MODALIDAD_META[modalidadItem(item)].inline;
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 2,
        borderTop: `1px solid ${BRAND.color.divider}`,
        py: "3mm",
      }}
    >
      <Box sx={{ color: BRAND.color.inkSoft }}>{nombre}</Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Box
          sx={{
            fontWeight: BRAND.font.weight.bold,
            color: BRAND.color.inkSoft,
          }}
        >
          {monto}
          {sigla ? ` ${sigla}` : ""}
        </Box>
        {inline && (
          <Box
            sx={{
              fontSize: BRAND.font.size.bodySmall,
              color: BRAND.color.grey50,
            }}
          >
            {inline}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Cuadrado negro dominante "Inversión Total" con los subtotales por
 * modalidad y moneda apilados verticalmente. Replica el cuadrado del PDF
 * (fondo oscuro, eyebrow, divisor corto y un bloque por modalidad+moneda).
 */
const CuadradoInversion = ({ totales }) => {
  const bloques = ORDEN_MODALIDAD.filter((clave) => totales[clave]).flatMap(
    (clave) =>
      Object.entries(totales[clave]).map(([moneda, valor]) => ({
        clave,
        moneda,
        valor,
      })),
  );
  const esMulti = bloques.length > 1;

  return (
    <Box
      sx={{
        flex: { xs: "0 0 auto", md: "0 0 220px" },
        width: { xs: "100%", md: 220 },
        minHeight: { md: 320 },
        bgcolor: BRAND.color.inkSoft,
        color: BRAND.color.paper,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 3,
        py: esMulti ? 4 : 5,
      }}
    >
      <Box
        sx={{
          fontSize: BRAND.font.size.eyebrow,
          color: BRAND.color.grey50,
          textTransform: "uppercase",
          letterSpacing: BRAND.font.letterSpacing.widest,
          fontWeight: BRAND.font.weight.medium,
          mb: 3,
        }}
      >
        Inversión Total
      </Box>
      <Box
        component="hr"
        sx={{
          width: "25%",
          border: 0,
          borderTop: `1px solid ${BRAND.color.primary}`,
          m: 0,
          mb: 3,
        }}
      />
      {bloques.map(({ clave, moneda, valor }, i) => {
        const { monto, sigla } = splitMontoMoneda(valor, moneda);
        const meta = MODALIDAD_META[clave];
        const unidad = meta.unidad ? ` ${meta.unidad}` : "";
        return (
          <Box
            key={`${clave}-${moneda}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              ...(i > 0 && {
                mt: 3,
                pt: 3,
                borderTop: "1px solid rgba(255, 255, 255, 0.12)",
              }),
            }}
          >
            <Box
              sx={{
                fontSize: BRAND.font.size.eyebrow,
                color: BRAND.color.grey50,
                textTransform: "uppercase",
                letterSpacing: BRAND.font.letterSpacing.wide,
                fontWeight: BRAND.font.weight.medium,
                mb: 0.5,
              }}
            >
              {meta.subtotal}
            </Box>
            <Box
              sx={{
                fontFamily: BRAND.font.family.display,
                fontSize: esMulti ? BRAND.font.size.h3 : BRAND.font.size.h2,
                fontWeight: BRAND.font.weight.black,
                color: BRAND.color.paper,
                lineHeight: BRAND.font.lineHeight.tight,
                letterSpacing: BRAND.font.letterSpacing.tight,
                mb: 0.5,
              }}
            >
              {monto}
            </Box>
            {sigla && (
              <Box
                sx={{
                  fontSize: BRAND.font.size.bodySmall,
                  fontWeight: BRAND.font.weight.bold,
                  color: BRAND.color.primary,
                  letterSpacing: BRAND.font.letterSpacing.wide,
                }}
              >
                {sigla}
                {unidad}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const DesgloseEconomico = ({ items }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    {GRUPOS_DESGLOSE.map(({ grupo, titulo }) => {
      const itemsGrupo = items
        .filter((it) => Number(it.valorConDescuento || 0) > 0)
        .filter((it) => MODALIDAD_META[modalidadItem(it)].grupo === grupo);
      if (itemsGrupo.length === 0) return null;
      return (
        <Box key={grupo} sx={{ mb: "6mm" }}>
          <DesgloseGrupoEyebrow>{titulo}</DesgloseGrupoEyebrow>
          {itemsGrupo.map((it) => (
            <DesgloseLineaItem key={it.id} item={it} />
          ))}
        </Box>
      );
    })}
  </Box>
);

const EconomicaBody = ({ propuesta, items, hayEconomica }) => {
  const totales = items.reduce((acc, it) => {
    // Solo items con monto > 0, en paridad con el PDF (fasesConMonto): un
    // item sin costo no debe crear una modalidad ni activar la nota.
    if (!(Number(it.valorConDescuento || 0) > 0)) return acc;
    const clave = modalidadItem(it);
    const m = formatearMonedaCliente(it.tipoMoneda) || "CLP";
    acc[clave] = acc[clave] || {};
    acc[clave][m] = (acc[clave][m] || 0) + Number(it.valorConDescuento || 0);
    return acc;
  }, {});
  const modalidadesPresentes = Object.keys(totales);

  return (
    <>
      {hayEconomica && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 4 },
            alignItems: "stretch",
            mb: { xs: 4, md: 5 },
          }}
        >
          <CuadradoInversion totales={totales} />
          <DesgloseEconomico items={items} />
        </Box>
      )}
      {modalidadesPresentes.length >= 2 && (
        <Box
          sx={{
            mt: "2mm",
            mb: { xs: 4, md: 5 },
            textAlign: "right",
            fontSize: BRAND.font.size.bodySmall,
            color: BRAND.color.grey50,
            fontStyle: "italic",
          }}
        >
          Los montos mensuales, anuales y de pago único se muestran por separado
          según su modalidad de cobro.
        </Box>
      )}
      {propuesta.condicionesPago && (
        <Box sx={{ mb: { xs: 4, md: 5 } }}>
          <EtiquetaCampo>Condiciones de pago</EtiquetaCampo>
          <ParrafosV2 texto={propuesta.condicionesPago} />
        </Box>
      )}
      {propuesta.duracionEstimada && (
        <Box>
          <EtiquetaCampo>Duración estimada</EtiquetaCampo>
          <Box>{propuesta.duracionEstimada}</Box>
        </Box>
      )}
    </>
  );
};

const CierreVendedor = ({ vendedor }) => {
  const nombre = [vendedor.nombre, vendedor.apellidos]
    .filter(Boolean)
    .join(" ");
  return (
    <Box
      sx={{
        textAlign: "center",
        mt: { xs: 6, md: 8 },
        pt: { xs: 6, md: 8 },
        borderTop: `1px solid ${BRAND.color.divider}`,
        fontFamily: BRAND.font.family.base,
        fontSize: BRAND.font.size.body,
        color: BRAND.color.inkSoft,
        lineHeight: BRAND.font.lineHeight.body,
      }}
    >
      <Box sx={{ mb: 2 }}>
        Quedamos a su entera disposición para cualquier consulta.
      </Box>
      <Box>
        Tu vendedor{" "}
        <Box component="strong" sx={{ fontWeight: BRAND.font.weight.bold }}>
          {nombre}
        </Box>{" "}
        está disponible
        {vendedor.correo && (
          <>
            {" "}
            en{" "}
            <Box
              component="a"
              href={`mailto:${vendedor.correo}`}
              sx={{
                color: BRAND.color.primary,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {vendedor.correo}
            </Box>
          </>
        )}
        {vendedor.telefono && <> · Teléfono: {vendedor.telefono}</>}.
      </Box>
    </Box>
  );
};

// ─── Componente principal ───────────────────────────────────────────────────

const ProposalSectionsV2 = ({
  propuesta,
  solicitante,
  vendedor,
  items,
  empresas,
  equipo,
  decisions,
  onChangeDecision,
  readOnly,
}) => {
  const tieneEquipo = Array.isArray(equipo) && equipo.length > 0;
  const tieneAntecedentes = !!(propuesta.antecedentes || propuesta.descripcion);
  const hayEconomica = items?.some(
    (it) => Number(it.valorConDescuento || 0) > 0,
  );
  const tieneEconomica =
    hayEconomica || !!(propuesta.condicionesPago || propuesta.duracionEstimada);

  // Lista canónica de secciones; las vacías quedan fuera. La numeración del
  // eyebrow se asigna en orden secuencial sobre las que sí se renderizan.
  const secciones = [
    propuesta.introduccion && {
      key: "introduccion",
      titulo: "Quiénes Somos",
      body: <ParrafosV2 texto={propuesta.introduccion} />,
    },
    propuesta.resumenEjecutivo && {
      key: "resumen",
      titulo: "Resumen Ejecutivo",
      body: <ParrafosV2 texto={propuesta.resumenEjecutivo} />,
    },
    tieneAntecedentes && {
      key: "antecedentes",
      titulo: "Antecedentes",
      body: (
        <>
          {propuesta.descripcion && (
            <ParrafosV2 texto={propuesta.descripcion} italica />
          )}
          {propuesta.antecedentes && (
            <ParrafosV2 texto={propuesta.antecedentes} />
          )}
        </>
      ),
    },
    propuesta.alcance && {
      key: "alcance",
      titulo: "Alcance del Servicio",
      body: <ParrafosV2 texto={propuesta.alcance} />,
    },
    {
      key: "metodologia",
      titulo: "Metodología de Trabajo",
      body: (
        <ItemsByCompanyV2
          items={items}
          empresas={empresas}
          decisions={decisions}
          onChangeDecision={onChangeDecision}
          readOnly={readOnly}
        />
      ),
    },
    tieneEquipo && {
      key: "equipo",
      titulo: "Equipo de Trabajo",
      body: <EquipoBody equipo={equipo} />,
    },
    tieneEconomica && {
      key: "economica",
      titulo: "Propuesta Económica",
      body: (
        <EconomicaBody
          propuesta={propuesta}
          items={items}
          hayEconomica={hayEconomica}
        />
      ),
    },
    propuesta.proximosPasos && {
      key: "proximos",
      titulo: "Próximos Pasos",
      body: (
        <>
          <Box
            sx={{
              fontFamily: BRAND.font.family.base,
              fontSize: BRAND.font.size.body,
              fontStyle: "italic",
              color: BRAND.color.grey70,
              mb: "6mm",
            }}
          >
            Acciones que ejecutaremos tras la aceptación de la propuesta.
          </Box>
          <ProximosPasosListaV2 texto={propuesta.proximosPasos} />
        </>
      ),
    },
    propuesta.clausulaConfidencialidad && {
      key: "confidencialidad",
      titulo: "Confidencialidad",
      body: <ParrafosV2 texto={propuesta.clausulaConfidencialidad} />,
    },
  ].filter(Boolean);

  const itemsIndice = secciones.map((s) => s.titulo);

  return (
    <Box
      sx={{
        fontFamily: BRAND.font.family.base,
        maxWidth: 980,
        mx: "auto",
        px: { xs: 3, md: 5 },
        py: { xs: 4, md: 6 },
      }}
    >
      {solicitante?.nombre && (
        <Box
          sx={{
            fontSize: BRAND.font.size.eyebrow,
            color: BRAND.color.grey70,
            textTransform: "uppercase",
            letterSpacing: BRAND.font.letterSpacing.wide,
            mb: { xs: 6, md: 8 },
          }}
        >
          Preparada para {solicitante.nombre}
        </Box>
      )}

      {itemsIndice.length > 0 && (
        <SeccionV2 eyebrow="Contenidos" titulo="Contenidos">
          <Box
            component="ol"
            sx={{
              listStyle: "none",
              p: 0,
              m: 0,
              display: "flex",
              flexDirection: "column",
              gap: "8mm",
            }}
          >
            {itemsIndice.map((label, i) => (
              <Box
                component="li"
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "8mm",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    flex: "0 0 auto",
                    fontSize: BRAND.font.size.eyebrow,
                    fontWeight: BRAND.font.weight.bold,
                    color: BRAND.color.primary,
                    letterSpacing: BRAND.font.letterSpacing.widest,
                  }}
                >
                  {padNumero(i + 1)}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: BRAND.font.size.body,
                    fontWeight: BRAND.font.weight.medium,
                    color: BRAND.color.ink,
                  }}
                >
                  {label}
                </Box>
              </Box>
            ))}
          </Box>
        </SeccionV2>
      )}

      {secciones.map((s, i) => (
        <SeccionV2 key={s.key} numero={i + 1} titulo={s.titulo}>
          {s.body}
        </SeccionV2>
      ))}

      {vendedor && <CierreVendedor vendedor={vendedor} />}
    </Box>
  );
};

// ─── PropTypes ──────────────────────────────────────────────────────────────

const equipoMember = PropTypes.shape({
  orden: PropTypes.number,
  rolDisplay: PropTypes.string,
  nombre: PropTypes.string,
  fotoUrl: PropTypes.string,
  descripcionExperiencia: PropTypes.string,
});

ParrafosV2.propTypes = {
  texto: PropTypes.string,
  italica: PropTypes.bool,
};

SeccionV2.propTypes = {
  numero: PropTypes.number,
  titulo: PropTypes.string.isRequired,
  eyebrow: PropTypes.string,
  children: PropTypes.node,
};

EtiquetaCampo.propTypes = {
  children: PropTypes.node.isRequired,
};

EquipoBody.propTypes = {
  equipo: PropTypes.arrayOf(equipoMember).isRequired,
};

DesgloseGrupoEyebrow.propTypes = { children: PropTypes.node.isRequired };
DesgloseLineaItem.propTypes = { item: PropTypes.object.isRequired };
DesgloseEconomico.propTypes = { items: PropTypes.array.isRequired };
CuadradoInversion.propTypes = { totales: PropTypes.object.isRequired };

EconomicaBody.propTypes = {
  propuesta: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  hayEconomica: PropTypes.bool.isRequired,
};

CierreVendedor.propTypes = {
  vendedor: PropTypes.object.isRequired,
};

ProposalSectionsV2.propTypes = {
  propuesta: PropTypes.object.isRequired,
  solicitante: PropTypes.shape({
    nombre: PropTypes.string,
    rut: PropTypes.string,
    correo: PropTypes.string,
    telefono: PropTypes.string,
  }),
  vendedor: PropTypes.shape({
    rut: PropTypes.string,
    nombre: PropTypes.string,
    apellidos: PropTypes.string,
    correo: PropTypes.string,
    telefono: PropTypes.string,
  }),
  items: PropTypes.array.isRequired,
  empresas: PropTypes.array.isRequired,
  equipo: PropTypes.array,
  decisions: PropTypes.object,
  onChangeDecision: PropTypes.func,
  readOnly: PropTypes.bool,
};

ProposalSectionsV2.defaultProps = {
  solicitante: null,
  vendedor: null,
  equipo: [],
  decisions: {},
  onChangeDecision: () => {},
  readOnly: false,
};

export default ProposalSectionsV2;
